
// File: src/services/media/contracts/normalizeClipMediaRef.ts
//
// normalizeClipMediaRef — tolerant clip → MediaRefV1 normalization
// ---------------------------------------------------------------
// Goals:
// - Works across evolving clip schemas (podcast clips, timeline clips, legacy)
// - Prefer explicit MediaRefV1 if provided
// - Deterministic heuristics (no network, no side effects)

import type { MediaKindV1, MediaRefV1 } from "./mediaRefV1";
import { normKind, normStr, normalizeMediaRefV1 } from "./mediaRefV1";

function looksLikeHttpUrl(s: string): boolean {
  return /^https?:\/\//i.test(s);
}
function looksLikeBlobOrDataUrl(s: string): boolean {
  return /^(blob:|data:)/i.test(s);
}
function looksLikeGsUri(s: string): boolean {
  return /^gs:\/\//i.test(s);
}

export type ClipLike = Record<string, any> | null | undefined;

/**
 * Normalize a clip-ish object to a canonical MediaRefV1.
 * Returns null if no usable media reference can be derived.
 */
export function normalizeClipMediaRef(clip: ClipLike, fallbackKind: MediaKindV1 = "audio"): MediaRefV1 | null {
  if (!clip || typeof clip !== "object") return null;

  const c: any = clip;

  // 1) If the clip already carries a MediaRefV1-ish object, trust it (normalize/validate).
  const direct =
    c.mediaRef ??
    c.mediaRefV1 ??
    c.media ??
    c.mediaSource ??
    c.source ??
    null;

  const directNorm = normalizeMediaRefV1(direct, { fallbackKind });
  if (directNorm) return directNorm;

  // 2) If the clip has an explicit provider + fields (legacy-ish), map to MediaRefV1.
  const provider = normStr(c.provider || c.mediaProvider, "").toLowerCase();
  const kind = normKind(c.kind || c.mediaKind, fallbackKind);
  const mime = normStr(c.mime || c.contentType || c.type, "");

  if (provider === "firebase") {
    const path = normStr(c.path || c.storagePath || c.objectPath || c.firebasePath, "");
    if (path) return { v: 1, provider: "firebase", kind, path, ...(mime ? { mime } : {}) };
  }

  if (provider === "registry") {
    const key = normStr(c.key || c.registryKey || c.assetId || c.mediaId, "");
    const registry = normStr(c.registry || c.namespace || c.ns, "");
    if (key) return { v: 1, provider: "registry", kind, key, ...(registry ? { registry } : {}), ...(mime ? { mime } : {}) };
  }

  if (provider === "gcs") {
    const gsUri = normStr(c.gsUri || c.gcsUri || c.gsURL, "");
    const bucket = normStr(c.bucket, "");
    const object = normStr(c.object || c.path, "");
    if (gsUri || (bucket && object)) {
      return { v: 1, provider: "gcs", kind, ...(gsUri ? { gsUri } : {}), ...(bucket ? { bucket } : {}), ...(object ? { object } : {}), ...(mime ? { mime } : {}) };
    }
  }

  if (provider === "url") {
    const url = normStr(c.url || c.href || c.mediaUrl || c.downloadURL, "");
    if (url) return { v: 1, provider: "url", kind, url, ...(mime ? { mime } : {}) };
  }

  if (provider === "static") {
    const path = normStr(c.path || c.src || c.assetPath, "");
    if (path) return { v: 1, provider: "static", kind, path, ...(mime ? { mime } : {}) };
  }

  // 3) Heuristic from common string fields
  const candidate =
    normStr(c.url, "") ||
    normStr(c.mediaUrl, "") ||
    normStr(c.downloadURL, "") ||
    normStr(c.src, "") ||
    normStr(c.path, "") ||
    normStr(c.storagePath, "") ||
    normStr(c.firebasePath, "") ||
    "";

  if (candidate) {
    if (looksLikeGsUri(candidate)) {
      return { v: 1, provider: "gcs", kind, gsUri: candidate, ...(mime ? { mime } : {}) };
    }
    if (looksLikeHttpUrl(candidate) || looksLikeBlobOrDataUrl(candidate)) {
      return { v: 1, provider: "url", kind, url: candidate, ...(mime ? { mime } : {}) };
    }
    // If it looks like a Firebase storage object path (no scheme, contains slash)
    if (!candidate.includes("://") && (candidate.includes("/") || candidate.includes("\\"))) {
      // Prefer firebase if the clip hints it; otherwise treat as static.
      const hintedFirebase = Boolean(c.storagePath || c.firebasePath || c.provider === "firebase" || c.mediaProvider === "firebase");
      if (hintedFirebase) return { v: 1, provider: "firebase", kind, path: candidate.replace(/\\/g, "/"), ...(mime ? { mime } : {}) };
      return { v: 1, provider: "static", kind, path: candidate, ...(mime ? { mime } : {}) };
    }
  }

  // 4) Registry key fallback
  const key = normStr(c.mediaId || c.assetId || c.registryKey || c.key, "");
  if (key) {
    const registry = normStr(c.registry || c.namespace || c.ns, "");
    return { v: 1, provider: "registry", kind, key, ...(registry ? { registry } : {}), ...(mime ? { mime } : {}) };
  }

  return null;
}
